import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Phone, Globe, FileText } from "lucide-react";
import { PageHero, Reveal, CTABanner } from "@/components/ui-blocks";

// A2P registration contacts
const A2P = {
  email: "Phosmanagement@gmail.com",
  phone: "+1 734-789-6568",
  website: "https://www.phosmanagementinfo.com",
};

export default function LegalLayout({ title, updated, sections, testid, otherPage }) {
  return (
    <div data-testid={testid}>
      <PageHero
        eyebrow="Legal"
        title={Array.isArray(title) ? title : [title]}
        sub={`Last Updated: ${updated}`}
      />

      {/* Meta row */}
      <section className="relative border-y border-white/5 bg-obsidian-900/40">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center justify-between gap-x-8 gap-y-3">
          <div className="flex items-center gap-3 text-ink-200 text-[11px] font-medium uppercase tracking-[0.18em]">
            <FileText size={12} className="text-accent" />
            <span>PHOS Management Inc.</span>
          </div>
          <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.18em]">
            <a href={`mailto:${A2P.email}`} className="text-ink-400 hover:text-accent flex items-center gap-2">
              <Mail size={12} /> {A2P.email}
            </a>
            <a href={`tel:${A2P.phone.replace(/\s|-/g, "")}`} className="text-ink-400 hover:text-accent flex items-center gap-2">
              <Phone size={12} /> {A2P.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="relative py-16 lg:py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          {/* Sticky TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <p className="overline mb-5">Contents</p>
              <nav className="border-l border-white/10">
                {sections.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    data-testid={`toc-${s.id}`}
                    className="group flex items-baseline gap-3 pl-4 py-2 border-l-2 border-transparent hover:border-accent hover:bg-white/[0.02] text-ink-400 hover:text-ink-50 transition-colors duration-300"
                  >
                    <span className="text-accent font-display text-xs w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm">{s.title}</span>
                  </a>
                ))}
              </nav>
              {otherPage && (
                <Link
                  to={otherPage.to}
                  data-testid="legal-other-page"
                  className="mt-8 block border border-white/10 hover:border-accent p-5 transition-colors duration-500"
                >
                  <span className="overline block mb-2">See also</span>
                  <span className="font-heading font-bold text-ink-50">{otherPage.label}</span>
                </Link>
              )}
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-8 lg:col-start-5">
            {sections.map((s, i) => (
              <Reveal key={s.id} delay={0.02 * i}>
                <section
                  id={s.id}
                  data-testid={`legal-section-${s.id}`}
                  className="scroll-mt-32 mb-16 lg:mb-20 pb-16 lg:pb-20 border-b border-white/5 last:border-none last:pb-0"
                >
                  <div className="flex items-baseline gap-6 mb-6">
                    <span className="font-display text-3xl lg:text-4xl text-accent leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-display text-2xl md:text-3xl lg:text-4xl uppercase leading-none tracking-tight">
                      {s.title}
                    </h2>
                  </div>
                  <div className="legal-prose space-y-5 text-ink-200 text-base leading-relaxed">
                    {s.body}
                  </div>
                </section>
              </Reveal>
            ))}

            {/* Contact block at the bottom */}
            <div className="border border-white/10 p-6 lg:p-10 mt-8">
              <p className="eyebrow-accent mb-4">Contact</p>
              <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight">PHOS Management Inc.</h3>
              <div className="mt-6 space-y-3 text-ink-200 text-sm">
                <a href={A2P.website} className="flex items-center gap-3 hover:text-accent">
                  <Globe size={16} className="text-accent" /> {A2P.website}
                </a>
                <a href={`mailto:${A2P.email}`} className="flex items-center gap-3 hover:text-accent">
                  <Mail size={16} className="text-accent" /> {A2P.email}
                </a>
                <a href={`tel:${A2P.phone.replace(/\s|-/g, "")}`} className="flex items-center gap-3 hover:text-accent">
                  <Phone size={16} className="text-accent" /> {A2P.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        eyebrow="Questions?"
        heading={<>Talk to a{"\n"}real person.</>}
        sub="Call or email us — we respond within one business day."
        cta="Call PHOS"
        ctaHref={`tel:${A2P.phone.replace(/\s|-/g, "")}`}
      />
    </div>
  );
}
